import {Component, effect, EventEmitter, input, Input, OnInit, Output} from '@angular/core';
import {TranslatePipe} from "@ngx-translate/core";
import {AsyncPipe, NgClass} from '@angular/common';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {UserHal} from '../../models/HAL/user-hal.model';
import {HttpClient} from '@angular/common/http';
import {SessionService} from '../../services/session.service';
import {debounceTime, distinctUntilChanged, map, Observable, shareReplay, startWith, switchMap} from 'rxjs';
import {MatFormField, MatPrefix} from '@angular/material/form-field';
import {MatAutocompleteModule, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {MatInput, MatInputModule} from '@angular/material/input';
import {UserCardComponent} from '../user-card/user-card.component';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {UserDtoModel} from '../../models/DTO/UserDto.model';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

@Component({
  selector: 'app-user-selection',
  imports: [
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    MatAutocompleteModule,
    MatAutocompleteTrigger,
    MatOption,
    MatInput,
    AsyncPipe,
    UserCardComponent,
    MatPrefix,
    MatIcon,
    MatButton,
    NgClass,
    MatProgressSpinner
  ],
  templateUrl: './user-selection.component.html',
  styleUrl: './user-selection.component.scss'
})
export class UserSelectionComponent implements OnInit {
  protected newUser: boolean = false;
  protected creatingUser: boolean = false;
  protected searchUserFormGroup: FormGroup;
  protected searchUserFormControl: FormControl = new FormControl('');
  protected createUserFormGroup: FormGroup;
  protected emailFormControl: FormControl = new FormControl('', [Validators.required, Validators.email]);
  protected firstNameFormControl: FormControl = new FormControl('', [Validators.required, Validators.maxLength(64)]);
  protected lastNameFormControl: FormControl = new FormControl('', [Validators.required, Validators.maxLength(64)]);
  protected phoneFormControl: FormControl = new FormControl('', [Validators.maxLength(64)]);
  protected companyFormControl: FormControl = new FormControl('', [Validators.maxLength(64)]);
  protected additionalInfoFormControl: FormControl = new FormControl('', [Validators.maxLength(256)]);
  protected filteredUsers: Observable<UserHal[]> = new Observable<UserHal[]>();
  protected chosenUser: UserHal | null = null;
  protected loadingSearchResult: boolean = false;

  @Input() canCreateUser: boolean = true;
  @Input() canSelectUser: boolean = true;
  @Input() teamId: string = "";
  @Input() caseId: string = "";
  prefilledUser = input<UserHal>();

  @Output() userSelected = new EventEmitter<UserHal>();
  @Output() userDeselected = new EventEmitter();
  @Output() creatingUserChange = new EventEmitter<boolean>();
  @Output() newUserChange = new EventEmitter<boolean>();

  constructor(
    private readonly sessionService: SessionService,
    private readonly httpClient: HttpClient) {
    this.searchUserFormGroup = new FormGroup({
      searchUserFormControl: this.searchUserFormControl
    });
    this.createUserFormGroup = new FormGroup({
      email: this.emailFormControl,
      firstName: this.firstNameFormControl,
      lastName: this.lastNameFormControl,
      phone: this.phoneFormControl,
      company: this.companyFormControl,
      additionalInfo: this.additionalInfoFormControl
    });
    this.loadingSearchResult = true;
    effect(() => {
      if (this.prefilledUser()) {
        this.selectUser(this.prefilledUser()!);
      }
    });
  }

  ngOnInit(): void {
    this.searchUserFormControl.valueChanges.pipe(
      debounceTime(250)
    ).subscribe(() => {
      this.loadingSearchResult = true;
    });
    this.filteredUsers = this.searchUserFormControl.valueChanges.pipe(
      startWith(''),
      map((value) => value ?? ''),
      distinctUntilChanged(),
      debounceTime(250),
      switchMap((searchParameter) =>
        this.executeFullTextSearch(searchParameter).pipe(
          map((response: { _embedded: { users: UserHal[] } }) => {
            return response._embedded?.users || [];
          })
        )
      ),
      shareReplay(1)
    );
    this.filteredUsers.subscribe(() => {
      this.loadingSearchResult = false;
    });
  }

  protected selectUser(user: UserHal) {
    this.newUser = false;
    this.newUserChange.emit(false);
    this.chosenUser = user;
    this.userSelected.emit(user);
  }

  protected deselectUser() {
    this.chosenUser = null;
    this.searchUserFormControl.setValue('');
    this.userDeselected.emit();
  }

  protected toggleNewUser(): void {
    this.newUser = !this.newUser;
    this.newUserChange.emit(this.newUser);
  }

  createUser() {
    this.setCreatingUser(true);
    this.createUserFormGroup.disable();
    let userToCreate: UserDtoModel = new UserDtoModel({
      email: this.emailFormControl.value,
      firstName: this.firstNameFormControl.value,
      lastName: this.lastNameFormControl.value,
      phone: this.phoneFormControl.value,
      company: this.companyFormControl.value,
      additionalInfo: this.additionalInfoFormControl.value
    });

    const sessionUserWithLinks = this.sessionService.getSession().user as UserHal;
    this.httpClient.post<UserHal>(sessionUserWithLinks._links.getLink('createUser')!.href, userToCreate).subscribe({
      next: (response: UserHal) => {
        this.selectUser(response);
        this.resetNewUserForm();
      },
      error: (error: any) => {
        this.resetNewUserForm();
        this.createUserFormGroup.enable();
        return error;
      }
    });
  }

  private executeFullTextSearch(searchParameter: string): Observable<any> {
    // Prevent search when the user is selected.
    if (typeof searchParameter !== 'string') {
      return new Observable<any>();
    }
    const user: UserHal = this.sessionService.getSession().user as UserHal;
    let searchUrl = user._links.getLink('fullTextSearch')!.href.replace("searchParamValue", searchParameter).replace(/\{&[^}]*}/, "");
    if (this.teamId !== "") {
      searchUrl += `&teamId=${this.teamId}`;
    }
    if (this.caseId !== "") {
      searchUrl += `&caseId=${this.caseId}`;
    }
    return this.httpClient.get(searchUrl);
  }

  private resetNewUserForm() {
    this.setCreatingUser(false);
    this.createUserFormGroup.reset();
    this.createUserFormGroup.enable();
  }

  private setCreatingUser(creating: boolean): void {
    this.creatingUser = creating;
    this.creatingUserChange.emit(creating);
  }
}
